import React, { createContext, useContext } from "react";
import { Box } from "@mui/material";

// MUI v7 replaced the classic Grid `item`/`xs`/`sm`/`md`/`lg` props with a new
// `size` API, silently ignoring the old props (they no longer do anything,
// which broke every Grid layout in this module - fields collapsed/overlapped).
// This is a drop-in, flexbox-based replacement that understands the OLD API,
// so existing `<Grid container>` / `<Grid item xs={..} md={..}>` markup keeps
// working unchanged - only the import needs to point here instead of
// "@mui/material".

const SpacingContext = createContext(0);

const BREAKPOINTS = { sm: 600, md: 900, lg: 1200, xl: 1536 };

const pct = (v) => `${(v / 12) * 100}%`;

const Grid = ({ container, item, xs, sm, md, lg, xl, spacing, sx, children, ...rest }) => {
  const ctxSpacing = useContext(SpacingContext);

  if (container) {
    const spacePx = (spacing || 0) * 8;
    return (
      <SpacingContext.Provider value={spacing || 0}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            margin: spacePx ? `-${spacePx / 2}px` : 0,
            width: spacePx ? `calc(100% + ${spacePx}px)` : "100%",
            ...sx,
          }}
          {...rest}
        >
          {children}
        </Box>
      </SpacingContext.Provider>
    );
  }

  const spacePx = ctxSpacing * 8;

  const mediaSx = {};
  Object.entries({ sm, md, lg, xl }).forEach(([bp, val]) => {
    if (val != null) {
      mediaSx[`@media (min-width:${BREAKPOINTS[bp]}px)`] = { flexBasis: pct(val), maxWidth: pct(val) };
    }
  });

  return (
    <Box
      sx={{
        padding: spacePx ? `${spacePx / 2}px` : 0,
        boxSizing: "border-box",
        flexBasis: xs != null ? pct(xs) : "100%",
        maxWidth: xs != null ? pct(xs) : "100%",
        minWidth: 0,
        ...mediaSx,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Grid;
